package br.com.duoma.duomaproducao.data

import br.com.duoma.duomaproducao.model.PartExecution
import br.com.duoma.duomaproducao.model.WorkCenter
import br.com.duoma.duomaproducao.model.WorkCenterExecution
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlin.collections.iterator
import kotlin.collections.mapIndexed


/**
 * For each unexecuted workcenter in the routing, finds the nearest cascading workcenter
 * that comes after it (higher routing order, cascades == true) and has an execution.
 * Synthesizes an execution with that cascader's date and quantity.
 */
fun applyExecutionCascade(
    executions: List<WorkCenterExecution>
): List<WorkCenterExecution> {

    if (executions.none { it.workCenter.cascades && it.dt_cadastro != null })
        return executions

    val sortedExecs = executions.sortedBy { it.ordem }
    val workCentersSorted = WorkCenter.entries.sortedBy { it.order }

    val result = sortedExecs.toMutableList()
    val existing = sortedExecs.map { it.workCenter }.toMutableSet()

    for (exec in sortedExecs) {
        if (exec.workCenter.cascades) {
            for (j in 0..exec.workCenter.order) {
                val wc = workCentersSorted[j]
                if (wc !in existing) {
                    result += exec.copy(
                        centro_trabalho = wc.toSevenName(),
                        celula_producao = "A_${exec.celula_producao}"
                    )
                    existing += wc
                }
            }
        }
    }
    return result.sortedBy { it.ordem }
}

fun businessDaysBetween(from: LocalDate, to: LocalDate): List<LocalDate> {
    val days = mutableListOf<LocalDate>()
    var current = from
    while (current < to) {
        if (current.dayOfWeek != DayOfWeek.SATURDAY && current.dayOfWeek != DayOfWeek.SUNDAY) {
            days.add(current)
        }
        current += DatePeriod(days = 1)
    }
    return days
}

fun spreadExecutions(executions: List<PartExecution>): List<PartExecution> {
    if (executions.isEmpty()) return emptyList()

    // Group into clumps by date, preserving insertion order
    val clumps = executions
        .groupBy { it.date }
//        .toSortedMap()

    val clumpDates = clumps.keys.toList()
    val result = mutableListOf<PartExecution>()

    clumps.entries.forEachIndexed { index, (date, items) ->
        val rangeStart = if (index == 0) date
        else clumpDates[index - 1] + DatePeriod(days = 1)
        val days = businessDaysBetween(rangeStart, date)

        if (days.isEmpty() || days.size == 1) {
            // All on same day, no spreading needed
            result.addAll(items.map { it.copy(date = date) })
            return@forEachIndexed
        }

        // Use a queue of the individual executions
        // and walk through days, filling each day until we move to the next
        val queue = ArrayDeque(items.flatMap { item ->
            // Expand each PartExecution into individual pieces
            List(item.quantity) { item.copy(quantity = 1) }
        })

        val totalPieces = queue.size
        val base = totalPieces / days.size
        val remainder = totalPieces % days.size

        // How many pieces go on each day
        val piecesPerDay = days.mapIndexed { i, day ->
            day to if (i < remainder) base + 1 else base
        }

        for ((day, count) in piecesPerDay) {
            var remaining = count
            while (remaining > 0 && queue.isNotEmpty()) {
                val piece = queue.removeFirst()
                result.add(piece.copy(date = day))
                remaining--
            }
        }
    }

    return result.groupBy { it.identifier()}.map { it.value.first().copy(quantity = it.value.sumOf { it.quantity }) }
}


fun <A, B, K> groupAndAggregate(
    input: Map<A, List<B>>,
    keySelector: (B) -> K,
    valueSelector: (B) -> Double,
    filter: (B) -> Boolean = { true }
): Map<K, Map<A, Double>> {

    val result = mutableMapOf<K, MutableMap<A, Double>>()

    for ((a, listB) in input) {
        for (b in listB) {
            if (!filter(b)) continue

            val key = keySelector(b)
            val inner = result.getOrPut(key) { mutableMapOf() }

            inner[a] = (inner[a] ?: 0.0) + valueSelector(b)
        }
    }

    return result
}
fun <K,V,R> Map<K,V>.mapIndexed(transform : (Int, K, V) -> R) : List<R> {
    var i = -1
    return this.map {
        i++
        transform(i, it.key, it.value)
    }
}
